import { z } from 'zod';

/**
 * Flow review + publish primitives. Backed by `flow_reviews` (one
 * row per review request) + `flow_review_decisions` (one row per
 * reviewer per review) and gated by `projects.review_policy`.
 *
 * Lifecycle:
 *
 *   draft edit -> request review (state=pending) -> reviewers vote ->
 *   N approvals (state=approved) -> publish (state=merged, copies
 *   draft_canvas_json -> canvas_json + bumps published_version).
 *
 *   - changes_requested: any reviewer's negative vote moves the
 *     review off pending. Author must edit the draft and re-request.
 *   - superseded: author edited the draft after opening the review.
 *     Reviewers' previous votes no longer count; a fresh review is
 *     required.
 */

export const FlowReviewStateSchema = z.enum(['pending', 'approved', 'changes_requested', 'superseded', 'merged']);
export type FlowReviewState = z.infer<typeof FlowReviewStateSchema>;

export const FlowReviewDecisionSchema = z.enum(['approved', 'changes_requested']);
export type FlowReviewDecision = z.infer<typeof FlowReviewDecisionSchema>;

export const FlowReviewDecisionRowSchema = z.object({
  reviewerId: z.string().uuid(),
  reviewerName: z.string(),
  decision: FlowReviewDecisionSchema,
  comment: z.string().nullable(),
  decidedAt: z.string(),
  /**
   * Phase 3: when true, the decision is shown for context but no
   * longer counts toward the approval gate. Set to true when the
   * author pushes a new revision after this vote was cast.
   */
  stale: z.boolean().default(false),
});
export type FlowReviewDecisionRow = z.infer<typeof FlowReviewDecisionRowSchema>;

export const FlowReviewSchema = z.object({
  id: z.string().uuid(),
  flowId: z.string().uuid(),
  /**
   * Branch this review targets. Nullable for V1 rows from before
   * migration 047 (those reviews implicitly merged into main from
   * the default branch).
   */
  branchId: z.string().uuid().nullable().optional(),
  orgId: z.string().uuid(),
  requestedBy: z.string().uuid(),
  requestedByName: z.string(),
  draftVersionAtRequest: z.number().int(),
  state: FlowReviewStateSchema,
  /** Optional human-readable summary (Markdown) for the review. */
  title: z.string().nullable().optional(),
  /** Optional rich description (Markdown). */
  description: z.string().nullable().optional(),
  comment: z.string().nullable(),
  /** Counts derived from `flow_review_decisions` for cheap rendering. */
  approvalsCount: z.number().int(),
  changesRequestedCount: z.number().int(),
  decisions: z.array(FlowReviewDecisionRowSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FlowReview = z.infer<typeof FlowReviewSchema>;

// ---- Request shapes ----

export const RequestReviewBodySchema = z.object({
  /**
   * Branch to open the review on. Required after migration 047.
   * The legacy "no branchId, default branch" alias is kept on the
   * server side but new clients should always pass it.
   */
  branchId: z.string().uuid().optional(),
  /** Short title shown in the inbox / banner. */
  title: z.string().max(200).optional(),
  /** Markdown description rendered on the review screen. */
  description: z.string().max(8000).optional(),
  /** Optional kickoff message for the reviewer (Markdown allowed). */
  comment: z.string().max(4000).optional(),
  /** Reviewers to invite up-front (Phase 2 hook). */
  reviewers: z
    .array(
      z.object({
        userId: z.string().uuid(),
        role: z.enum(['required', 'optional']).default('required'),
      }),
    )
    .max(20)
    .optional(),
});
export type RequestReviewBody = z.infer<typeof RequestReviewBodySchema>;

export const SubmitDecisionBodySchema = z.object({
  decision: FlowReviewDecisionSchema,
  comment: z.string().max(4000).optional(),
});
export type SubmitDecisionBody = z.infer<typeof SubmitDecisionBodySchema>;

/**
 * Empty-body request — publishing is purely "promote the current
 * approved draft". The server cross-checks
 * `draft_version === pending review's draft_version_at_request` so
 * an interleaved edit can't sneak past the gate.
 */
export const PublishFlowBodySchema = z.object({}).strict();
export type PublishFlowBody = z.infer<typeof PublishFlowBodySchema>;

// ---- Response shapes ----

export const FlowReviewResponseSchema = z.object({
  review: FlowReviewSchema,
});
export type FlowReviewResponse = z.infer<typeof FlowReviewResponseSchema>;

export const ListFlowReviewsResponseSchema = z.object({
  reviews: z.array(FlowReviewSchema),
});
export type ListFlowReviewsResponse = z.infer<typeof ListFlowReviewsResponseSchema>;

// ---- Project review policy (lives on projects.review_policy JSONB) ----
//
// Defaults match the migration's column default; an existing org
// keeps "publish freely" until an admin opts a project in.

export const ReviewPolicySchema = z.object({
  requiresReview: z.boolean().default(false),
  /** 1..5 in v1; the UI exposes a stepper. */
  minApprovers: z.number().int().min(1).max(5).default(1),
  /** When false, the author of a review cannot vote on it. */
  allowSelfApprove: z.boolean().default(false),
  /**
   * Migration 048: project-level default reviewer lists. Auto-seeded
   * onto every new review on this project as required/optional.
   * Editors can still add or remove reviewers per review; these are
   * just a starting point so the same names don't get retyped.
   */
  defaultRequiredReviewers: z.array(z.string().uuid()).default([]),
  defaultOptionalReviewers: z.array(z.string().uuid()).default([]),
});
export type ReviewPolicy = z.infer<typeof ReviewPolicySchema>;

export const DEFAULT_REVIEW_POLICY: ReviewPolicy = {
  requiresReview: false,
  minApprovers: 1,
  allowSelfApprove: false,
  defaultRequiredReviewers: [],
  defaultOptionalReviewers: [],
};

// ---- Published version history ----

export const FlowVersionSchema = z.object({
  id: z.string().uuid(),
  flowId: z.string().uuid(),
  version: z.number().int(),
  canvasJson: z.record(z.unknown()),
  publishedBy: z.string().uuid().nullable(),
  publishedByName: z.string().nullable(),
  publishedAt: z.string(),
  reviewId: z.string().uuid().nullable(),
});
export type FlowVersion = z.infer<typeof FlowVersionSchema>;

export const ListFlowVersionsResponseSchema = z.object({
  versions: z.array(FlowVersionSchema),
});
export type ListFlowVersionsResponse = z.infer<typeof ListFlowVersionsResponseSchema>;
